import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
  type MRT_ColumnDef,
} from 'material-react-table';

import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
  lighten,
} from '@mui/material';

import { MembersRegistryTableData } from '../../../interface/IMembersRegistry';

import helper from '../../../helpers/index';


import iconEdit from '../../../assets/edit-icon.png';
import iconDelete from '../../../assets/delete.png';
import iconView from '../../../assets/visible.png';



//example data type


const columnHelper = createMRTColumnHelper<MembersRegistryTableData>();

interface AdminRegistryTableProps {
  data: MembersRegistryTableData[];
  deleteMemberButtonClick: (memberId: number, name: string) => void;
  isReadOnly: boolean;
}


const AdminRegistryTable: React.FC<AdminRegistryTableProps> = ({ data, deleteMemberButtonClick, isReadOnly }) => {

  const navigate = useNavigate();

  function UpdateMemberButtonClick(memberId: number) {
    navigate('/Admin/UpdateMember/' + memberId);

  }

  function ViewMemberButtonClick(memberId: number) {
    navigate('/Admin/ViewMember/' + memberId);
  }

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<MembersRegistryTableData>[]>(
    () => [
      {
        accessorFn: (row: MembersRegistryTableData) => `${row.firstname} ${row.lastname}`,
        id: 'fullName',
        header: 'Full Name',
        enableSorting: true,
        enableFiltering: true
      },
      {
        // accessorKey: 'membershipId',
        accessorFn: (row: MembersRegistryTableData) => `#${row.membershipId}`,
        header: 'Member ID',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.membershipId;
          const idB = rowB.original.membershipId;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        // accessorKey: 'dateAccepted',
        accessorFn: (row: MembersRegistryTableData) => {

          if (row.dateAccepted) {
            return `${helper.formatLongDate(row.dateAccepted)}`
          }
          else {
            return "";

          }


        },
        header: 'Date Accepted',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const firstData = new Date(rowA.original.dateAccepted).getTime() || 0;
          const secondData = new Date(rowB.original.dateAccepted).getTime() || 0;
          // Perform numeric comparison
          return Number(firstData) - Number(secondData);
        }
      },
      {
        accessorKey: 'email',
        header: 'Email'
      },
      {
        // accessorKey: 'verificationStatus', 
        accessorFn: (row: MembersRegistryTableData) => {
          if (row.verificationStatus) {
            return <span style={{ fontWeight: 'bold', color: 'green' }}>Verified</span>;
          } else {
            return <span style={{ fontWeight: 'bold', color: 'orange' }}>Unverified</span>;
          }
        },
        header: 'Verification Status',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.verificationStatus;
          const idB = rowB.original.verificationStatus;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        accessorFn: (row: MembersRegistryTableData) => {
          if (row.accountStatus) {
            return <span style={{ fontWeight: 'bold', color: 'green' }}>Complete</span>;
          } else {
            return <span style={{ fontWeight: 'bold', color: 'red' }}>Incomplete</span>;
          }
        },
        header: 'Account Status',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.accountStatus;
          const idB = rowB.original.accountStatus;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        header: 'Actions',
        Cell: ({ renderedCellValue, row }) => (
          <Box className='table-btn'>
            <Tooltip title="Edit">
              <Button
                className="btn-icon-1"
                disabled={isReadOnly}
                onClick={() => UpdateMemberButtonClick(row.original.memberId)}
              >
                <img className='responsive-image' src={iconEdit} />
              </Button>
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                disabled={isReadOnly}
                className="btn-icon-1"
                onClick={() => deleteMemberButtonClick(row.original.memberId, row.original.firstname + " " + row.original.lastname)}
              >
                <img className='responsive-image' src={iconDelete} />
              </Button>
            </Tooltip>
            <Tooltip title="Member Overview">
              <Button
                className="btn-icon-1"
                onClick={() => ViewMemberButtonClick(row.original.memberId)}
              >
                <img className='responsive-image' src={iconView} />
              </Button>
            </Tooltip>
          </Box>
        )
      }
    ],
    [isReadOnly],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    muiTablePaperProps: {
      elevation: 1,
      sx: {
        boxShadow: 'none',
        width: '100%',
        background: 'transparent',
        display: 'grid',

      }
    }
  });

  return <MaterialReactTable table={table} />;
};

export default AdminRegistryTable;
